import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useAIAgents } from '@/hooks/useAIAgents';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  agent_type: z.enum(['data_validator', 'anomaly_detector', 'invoice_processor', 'cost_optimizer', 'report_generator', 'collection_manager', 'custom']),
  is_scheduled: z.boolean().default(false),
});

interface CreateAgentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CreateAgentDialog = ({ open, onOpenChange }: CreateAgentDialogProps) => {
  const { createAgent, isCreating } = useAIAgents();
  const [currentStep, setCurrentStep] = useState('basic');
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      description: '',
      agent_type: 'data_validator',
      is_scheduled: false,
    },
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    createAgent({
      name: values.name,
      description: values.description,
      agent_type: values.agent_type,
      is_scheduled: values.is_scheduled,
      configuration: getDefaultConfiguration(values.agent_type),
      triggers: getDefaultTriggers(values.agent_type),
      actions: getDefaultActions(values.agent_type),
      model_config: {
        provider: 'openai',
        model: 'gpt-4o-mini',
        temperature: 0.1,
      },
    });
    form.reset();
    onOpenChange(false);
  };

  const getDefaultConfiguration = (type: string) => {
    switch (type) {
      case 'data_validator':
        return {
          validation_rules: ['amount_format', 'date_format', 'required_fields'],
          confidence_threshold: 0.85,
        };
      case 'anomaly_detector':
        return {
          detection_methods: ['statistical', 'ml_based'],
          sensitivity: 'medium',
        };
      case 'invoice_processor':
        return {
          extract_fields: ['amount', 'date', 'vendor', 'invoice_number'],
          ocr_provider: 'default',
        };
      case 'cost_optimizer':
        return {
          optimization_targets: ['reduce_costs', 'improve_efficiency'],
          threshold_percentage: 10,
        };
      case 'report_generator':
        return {
          report_types: ['monthly_summary', 'cost_analysis'],
          auto_schedule: true,
        };
      case 'collection_manager':
        return {
          escalation_levels: 3,
          grace_period_days: 7,
        };
      default:
        return {};
    }
  };

  const getDefaultTriggers = (type: string) => {
    switch (type) {
      case 'data_validator':
        return { events: ['bill_processed'], conditions: { status: 'completed' } };
      case 'anomaly_detector':
        return { schedule: 'daily', events: ['new_bill'] };
      case 'invoice_processor':
        return { events: ['file_uploaded'], file_types: ['pdf', 'jpg', 'png'] };
      case 'cost_optimizer':
        return { schedule: 'weekly', events: ['billing_cycle_complete'] };
      case 'report_generator':
        return { schedule: 'monthly', events: ['month_end'] };
      case 'collection_manager':
        return { events: ['payment_overdue'], schedule: 'daily' };
      default:
        return {};
    }
  };

  const getDefaultActions = (type: string) => {
    switch (type) {
      case 'data_validator':
        return { on_validation_fail: 'flag_for_review', on_validation_pass: 'auto_approve' };
      case 'anomaly_detector':
        return { on_anomaly: 'create_alert', notify_users: true };
      case 'invoice_processor':
        return { extract_data: true, create_bill_record: true };
      case 'cost_optimizer':
        return { generate_recommendations: true, auto_apply_savings: false };
      case 'report_generator':
        return { generate_charts: true, email_reports: true };
      case 'collection_manager':
        return { send_reminders: true, escalate_cases: true };
      default:
        return {};
    }
  };

  const agentTypes = [
    {
      value: 'data_validator',
      label: 'Data Validator',
      description: 'Validates extracted data for accuracy and completeness',
      icon: '✓',
    },
    {
      value: 'anomaly_detector',
      label: 'Anomaly Detector',
      description: 'Detects unusual patterns in billing data',
      icon: '🔍',
    },
    {
      value: 'invoice_processor',
      label: 'Invoice Processor',
      description: 'Processes and extracts data from invoices',
      icon: '📄',
    },
    {
      value: 'cost_optimizer',
      label: 'Cost Optimizer',
      description: 'Analyzes and optimizes costs and usage',
      icon: '💰',
    },
    {
      value: 'report_generator',
      label: 'Report Generator',
      description: 'Generates automated reports and insights',
      icon: '📊',
    },
    {
      value: 'collection_manager',
      label: 'Collection Manager',
      description: 'Manages payment collection workflows',
      icon: '📞',
    },
    {
      value: 'custom',
      label: 'Custom Agent',
      description: 'Create a custom agent with your own configuration',
      icon: '🤖',
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create AI Agent</DialogTitle>
          <DialogDescription>
            Set up a new AI agent to automate your billing processes
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <Tabs value={currentStep} onValueChange={setCurrentStep}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="basic">Basic Info</TabsTrigger>
                <TabsTrigger value="type">Agent Type</TabsTrigger>
              </TabsList>

              <TabsContent value="basic" className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Agent Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter agent name" {...field} />
                      </FormControl>
                      <FormDescription>
                        Give your agent a descriptive name
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Describe what this agent does..."
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Optional description of the agent's purpose
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="is_scheduled"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">
                          Scheduled Execution
                        </FormLabel>
                        <FormDescription>
                          Enable automatic execution based on schedule
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </TabsContent>

              <TabsContent value="type" className="space-y-4">
                <FormField
                  control={form.control}
                  name="agent_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Agent Type</FormLabel>
                      <FormDescription>
                        Choose the type of agent based on your needs
                      </FormDescription>
                      <div className="grid gap-3 mt-4">
                        {agentTypes.map((type) => (
                          <Card
                            key={type.value}
                            className={`cursor-pointer transition-colors ${
                              field.value === type.value
                                ? 'ring-2 ring-primary bg-primary/5'
                                : 'hover:bg-muted/50'
                            }`}
                            onClick={() => field.onChange(type.value)}
                          >
                            <CardHeader className="pb-2">
                              <div className="flex items-center space-x-3">
                                <div className="text-2xl">{type.icon}</div>
                                <div>
                                  <CardTitle className="text-base">{type.label}</CardTitle>
                                  <CardDescription className="text-sm">
                                    {type.description}
                                  </CardDescription>
                                </div>
                              </div>
                            </CardHeader>
                            {field.value === type.value && (
                              <CardContent className="pt-0">
                                <Badge variant="secondary" className="text-xs">
                                  Selected
                                </Badge>
                              </CardContent>
                            )}
                          </Card>
                        ))}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TabsContent>
            </Tabs>

            <div className="flex justify-between">
              {currentStep === 'type' && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCurrentStep('basic')}
                >
                  Previous
                </Button>
              )}
              
              <div className="ml-auto flex space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                
                {currentStep === 'basic' ? (
                  <Button
                    type="button"
                    onClick={() => setCurrentStep('type')}
                  >
                    Next
                  </Button>
                ) : (
                  <Button type="submit" disabled={isCreating}>
                    {isCreating ? 'Creating...' : 'Create Agent'}
                  </Button>
                )}
              </div>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};